<template>
  <div>
    <label v-if="label" class="mb-0.5 block text-body-s-bold">{{ label }}</label>
    <div class="flex min-h-[50px] items-center rounded-sm border border-strong p-2">
      <div class="flex grow flex-wrap items-center gap-2">
        <SvgIcon :path="mdiMagnify" :size="24" class="grow-0 text-subtle" />
        <span
          v-for="name in searchTerms"
          :key="name"
          class="flex grow-0 items-center gap-1 rounded-full bg-surface-inverse py-1 pr-1 pl-2 text-label-s text-inverse"
        >
          {{ name }}
          <SvgIcon
            :path="mdiCloseCircle"
            :data-testid="`btn-remove-term-${name}`"
            :size="24"
            class="cursor-pointer text-disabled"
            @click.stop="removeTerm(name)"
          />
        </span>
        <input
          v-model="searchTerm"
          type="text"
          class="grow bg-transparent outline-none"
          :placeholder="searchTerms.length ? undefined : placeholder"
          data-testid="invitee-search-field"
          @keyup="keyUp"
          @keydown.backspace="keyDownPressed"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiMagnify, mdiCloseCircle } from '@mdi/js'
import { ref, watch } from 'vue'

const props = defineProps<{
  modelValue?: string[]
  placeholder: string
  label?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

function keyUp(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    addTerm()
  }
  if (event.key === 'Backspace') {
    keyDownReleased()
  }
}

const deletingTermLetters = ref(false)
const searchTerms = ref<string[]>(props.modelValue || [])
const searchTerm = ref('')

const addTerm = () => {
  const term = searchTerm.value.trim()

  if (!term) return

  const names = searchTerms.value.slice()
  names.push(term)

  emit('update:modelValue', [...new Set(names)])

  searchTerm.value = ''
}

watch(
  () => props.modelValue,
  (newValue) => {
    searchTerms.value = newValue || []
  },
)

const removeTerm = (name: string) => {
  const terms = searchTerms.value.filter((existingName) => existingName !== name)
  emit('update:modelValue', terms)
}

const keyDownPressed = () => {
  // Check length of string here, because the last letter (if present) is not
  // deleted yet.
  if (searchTerm.value.length) deletingTermLetters.value = true
}

const keyDownReleased = () => {
  if (deletingTermLetters.value) {
    deletingTermLetters.value = false
    return
  }

  deletingTermLetters.value = false

  const lastTerm = searchTerms.value[searchTerms.value.length - 1]

  if (lastTerm) removeTerm(lastTerm)
}
</script>
